import React, { useEffect } from 'react';
import { Box, Typography, Grid, Card, CardContent, TextField, FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material';
import { GetPolicyRisks } from '../../store/user/policyDetails/policyDetailsActions';
import { useAppSelector } from '../../store/hooks';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import PersonalInfoStyles from "./PersonalInfoStyles";
import CustomSelect from "../../components/common/Select";

interface PersonalInfoProps {
  formData: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    role: string;
    relationshipToInsured: string;
    filingOnBehalf: string;
    policyNumber: string;
    policyType: string;
    insuredName: string;
    insuredContact: string;
    effectiveDate: string;
    expiryDate: string;
    driverType: string;
  };
  selectedClaimType: 'first-party' | 'third-party' | null;
  selectedClaimantType: 'self' | 'others' | 'agent' | null;
  onFormChange: (field: string, value: any) => void;
  onClaimantTypeSelect: (type: 'self' | 'others' | 'agent') => void;
  validatePhoneNumber:(phone: string) => boolean;
  validateEmail:(email: string)=>boolean;
  validateAlphanumeric:(value: string)=>boolean;
}

const PersonalInfo: React.FC<PersonalInfoProps> = ({
  formData,
  selectedClaimType,
  selectedClaimantType,
  onFormChange,
  onClaimantTypeSelect,
  validatePhoneNumber,
  validateEmail,
  validateAlphanumeric
}) => {
  const getAvailableClaimantTypes = () => {
    if (selectedClaimType === 'third-party') {
      return ['self', 'agent'];
    }
    return ['self', 'others', 'agent'];
  };
  
  const availableClaimantTypes = getAvailableClaimantTypes();
  const formatPhoneNumber = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 0) return '';
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    onFormChange("phone", formattedPhone);
  };

  const isRelationshipRequired = () => {
    return selectedClaimType === 'first-party' && selectedClaimantType === 'others';
  };

  const isDriverTypeRequired = () => {
    return formData.role === 'driver' && selectedClaimType === 'first-party';
  };

  const showDriverType = () => {
    return selectedClaimType === 'first-party'&& formData.role === 'driver';
  };

  const showFilingOnBehalf = () => {
    return selectedClaimantType === 'agent';
  };

  return (
    <Box sx={PersonalInfoStyles.container}>
      <Box sx={PersonalInfoStyles.card}>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={PersonalInfoStyles.cardTitle}
          id="personalinfo_claimanttype-title"
          data-test-id="test-personalinfo_claimanttype-title"
          aria-label="Claimant Type Section"
        >
          Claimant Type
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          gutterBottom 
          sx={PersonalInfoStyles.cardSubtitle}
          id="personalinfo_claimanttype-description"
          data-test-id="test-personalinfo_claimanttype-description"
        >
          {selectedClaimType === "third-party" ? "Third Party Claim - Filing against another's policy" : "First Party Claim - Filing on your own policy"}
        </Typography>
      </Box>

      <Typography 
        variant="h6" 
        sx={PersonalInfoStyles.sectionTitle}
        id="personalinfo_filing-title"
        data-test-id="test-personalinfo_filing-title"
        aria-label="Who is filing this claim section"
      >
        Who is filing this claim?
      </Typography>
      <Grid container spacing={3} sx={PersonalInfoStyles.gridSection}>
        {availableClaimantTypes.includes('self') && (
          <Grid item xs={12} md={4}>
            <Card 
              sx={PersonalInfoStyles.claimantCard(selectedClaimantType === "self")}
              onClick={() => onClaimantTypeSelect("self")}
              id="personalinfo_claimanttype-self-card"
              data-test-id="test-personalinfo_claimanttype-self-card"
              aria-label="Select self as claimant type"
            >
              <CardContent sx={PersonalInfoStyles.claimantCardContent}>
                <Box>
                  <PersonOutlineOutlinedIcon sx={PersonalInfoStyles.claimantIcon}/>
                </Box>
                <Typography variant="h6" gutterBottom sx={PersonalInfoStyles.claimantCardTitle}>
                  Self
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={PersonalInfoStyles.claimantCardDesc}>
                  I am filing this claim for myself.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
        {availableClaimantTypes.includes('others') && (
          <Grid item xs={12} md={4}>
            <Card 
              sx={PersonalInfoStyles.claimantCard(selectedClaimantType === "others")}
              onClick={() => onClaimantTypeSelect("others")}
              id="personalinfo_claimanttype-others-card"
              data-test-id="test-personalinfo_claimanttype-others-card"
              aria-label="Select others as claimant type"
            >
              <CardContent sx={PersonalInfoStyles.claimantCardContent}>
                <Box>
                  <PeopleAltOutlinedIcon sx={PersonalInfoStyles.claimantIcon}/>
                </Box>
                <Typography variant="h6" gutterBottom sx={PersonalInfoStyles.claimantCardTitle}>
                  Others
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={PersonalInfoStyles.claimantCardDesc}>
                  I am filing on behalf of someone else.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
        {availableClaimantTypes.includes('agent') && (
          <Grid item xs={12} md={4}>
            <Card 
              sx={PersonalInfoStyles.claimantCard(selectedClaimantType === "agent")}
              onClick={() => onClaimantTypeSelect("agent")}
              id="personalinfo_claimanttype-agent-card"
              data-test-id="test-personalinfo_claimanttype-agent-card"
              aria-label="Select agent as claimant type"
            >
              <CardContent sx={PersonalInfoStyles.claimantCardContent}>
                <Box>
                  <PersonOutlineOutlinedIcon sx={PersonalInfoStyles.claimantIcon}/> <BusinessCenterOutlinedIcon sx={PersonalInfoStyles.claimantIcon} />
                </Box>
                <Typography variant="h6" gutterBottom sx={PersonalInfoStyles.claimantCardTitle}>
                  Agent
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={PersonalInfoStyles.claimantCardDesc}>
                  I am an agent filing on behalf of a customer.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="First Name"
            value={formData.firstName}
            onChange={(e) => onFormChange("firstName", e.target.value)}
            disabled={selectedClaimType === 'first-party'}
            InputProps={{
              readOnly: selectedClaimType === 'first-party',
              style: { fontFamily: "var(--font-noto-sans)" }
            }}
            InputLabelProps={{
              style: { fontFamily: "var(--font-noto-sans)" }
            }}
            id="personalinfo_firstname-input"
            data-test-id="test-personalinfo_firstname-input"
            aria-label="Enter first name"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Last Name"
            value={formData.lastName}
            onChange={(e) => onFormChange("lastName", e.target.value)}
            disabled={selectedClaimType === 'first-party'}
            InputProps={{
              readOnly: selectedClaimType === 'first-party',
              style: { fontFamily: "var(--font-noto-sans)" }
            }}
            InputLabelProps={{
              style: { fontFamily: "var(--font-noto-sans)" }
            }}
            id="personalinfo_lastname-input"
            data-test-id="test-personalinfo_lastname-input"
            aria-label="Enter last name"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => onFormChange("email", e.target.value)}
            disabled={selectedClaimType === 'first-party'}
            InputProps={{
              readOnly: selectedClaimType === 'first-party',
              style: { fontFamily: "var(--font-noto-sans)" }
            }}
            error={formData.email !== '' && !validateEmail(formData.email)}
            helperText={formData.email !== '' && !validateEmail(formData.email) 
              ? 'Invalid email format' 
              : ''
            }
            id="personalinfo_email-input"
            data-test-id="test-personalinfo_email-input"
            aria-label="Enter email address"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Phone"
            value={formData.phone}
            onChange={handlePhoneChange}
            disabled={selectedClaimType === 'first-party'}
            InputProps={{
              readOnly: selectedClaimType === 'first-party',
              style: { fontFamily: "var(--font-noto-sans)" }
            }}
            error={formData.phone !== '' && !validatePhoneNumber(formData.phone)}
            helperText={formData.phone !== '' && !validatePhoneNumber(formData.phone) 
              ? 'Must be a valid 10-digit number' 
              : ""
            }
            id="personalinfo_phone-input"
            data-test-id="test-personalinfo_phone-input"
            aria-label="Enter phone number"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomSelect
            label="Role"
            options={[
              { value: "driver", label: "Driver" },
              { value: "pedestrian", label: "Pedestrian" },
              { value: "passenger", label: "Passenger" },
              { value: "property-owner", label: "Property Owner" }
            ]}
            value={formData.role}
            onChange={(value) => onFormChange("role", value)}
            required
            id="personalinfo_role-select"
            data-test-id="test-personalinfo_role-select"
          />
        </Grid>

        {showDriverType() && (
          <Grid item xs={12} md={6}>
            <CustomSelect
              label="Driver Type"
              options={[
                { value: "self", label: "Self" },
                { value: "permissive", label: "Permissive" },
                { value: "non-permissive", label: "Non-permissive" }
              ]}
              value={formData.driverType}
              onChange={(value) => onFormChange("driverType", value)}
              disabled={selectedClaimType === 'third-party'}
              required={isDriverTypeRequired()}
              id="personalinfo_drivertype-select"
              data-test-id="test-personalinfo_drivertype-select"
            />
            {selectedClaimType === 'third-party' && (
              <FormHelperText 
                sx={{ fontFamily: "var(--font-noto-sans)" }}
                id="personalinfo_drivertype-helper"
                data-test-id="test-personalinfo_drivertype-helper"
              >
                Not required for Third Party claims
              </FormHelperText>
            )}
          </Grid>
        )}

        {isRelationshipRequired() && (
          <Grid item xs={12} md={6}>
            <CustomSelect
              label="Relationship to Insured"
              options={[
               // { value: "self", label: "Self" },
                { value: "spouse", label: "Spouse" },
                { value: "passenger", label: "Passenger" },
                { value: "other", label: "Other" }
              ]}
              value={formData.relationshipToInsured}
              onChange={(value) => onFormChange("relationshipToInsured", value)}
              required
              id="personalinfo_relationship-select"
              data-test-id="test-personalinfo_relationship-select"
            />
          </Grid>
        )}

        {showFilingOnBehalf() && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Filing on behalf (Agent Id)"
              value={formData.filingOnBehalf}
              onChange={(e) => onFormChange("filingOnBehalf", e.target.value)}
              sx={{ fontFamily: "var(--font-noto-sans)" }}
              id="personalinfo_agentid-input"
              data-test-id="test-personalinfo_agentid-input"
              aria-label="Enter agent ID"
            />
          </Grid>
        )}
      </Grid>

      <Typography 
        variant="h6" 
        sx={PersonalInfoStyles.sectionTitle}
        id="personalinfo_policy-title"
        data-test-id="test-personalinfo_policy-title"
        aria-label="Policy Information Section"
      >
        Policy Information
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Policy Number"
            value={formData.policyNumber}
            onChange={(e) => onFormChange("policyNumber", e.target.value)}
            sx={{ fontFamily: "var(--font-noto-sans)" }}
             disabled={selectedClaimType === 'first-party'}
            InputProps={{
              readOnly: selectedClaimType === 'first-party',
              style: { fontFamily: "var(--font-noto-sans)" }
            }}
            error={formData.policyNumber === '' && !validateAlphanumeric(formData.policyNumber)}
            helperText={formData.policyNumber !== '' && !validateAlphanumeric(formData.policyNumber) 
              ? 'Must be a Alphanumeric' 
              : ""
            }
            id="personalinfo_policynumber-input"
            data-test-id="test-personalinfo_policynumber-input"
            aria-label="Enter policy number"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Policy Type"
            value={formData.policyType}
            onChange={(e) => onFormChange("policyType", e.target.value)}
            sx={{ fontFamily: "var(--font-noto-sans)" }}
            disabled={selectedClaimType === 'first-party'}
            InputProps={{
              readOnly: selectedClaimType === 'first-party',
              style: { fontFamily: "var(--font-noto-sans)" }
            }}
            id="personalinfo_policytype-input"
            data-test-id="test-personalinfo_policytype-input"
            aria-label="Enter policy type"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required={selectedClaimType === 'first-party'}
            label="Insured Name"
            value={formData.insuredName}
            onChange={(e) => onFormChange("insuredName", e.target.value)}
            sx={{ fontFamily: "var(--font-noto-sans)" }}
            disabled={selectedClaimType === 'first-party'}
            InputProps={{
              readOnly: selectedClaimType === 'first-party',
              style: { fontFamily: "var(--font-noto-sans)" }
            }}
            id="personalinfo_insuredname-input"
            data-test-id="test-personalinfo_insuredname-input"
            aria-label="Enter insured name"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Insured Contact Info"
            value={formData.insuredContact}
            onChange={(e) => onFormChange("insuredContact", e.target.value)}
            placeholder="Optional"
            sx={{ fontFamily: "var(--font-noto-sans)" }}
            id="personalinfo_insuredcontact-input"
            data-test-id="test-personalinfo_insuredcontact-input"
            aria-label="Enter insured contact information"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Effective Date"
            type="date"
            value={formData.effectiveDate}
            onChange={(e) => onFormChange("effectiveDate", e.target.value)}
            InputLabelProps={{ shrink: true, style: { fontFamily: "var(--font-noto-sans)" } }}
            disabled={selectedClaimType === 'first-party'}
            InputProps={{
              readOnly: selectedClaimType === 'first-party',
              style: { fontFamily: "var(--font-noto-sans)" }
            }}
            id="personalinfo_effectivedate-input"
            data-test-id="test-personalinfo_effectivedate-input"
            aria-label="Select effective date"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Expiry Date"
            type="date"
            value={formData.expiryDate}
            onChange={(e) => onFormChange("expiryDate", e.target.value)}
            InputLabelProps={{ shrink: true, style: { fontFamily: "var(--font-noto-sans)" } }}
            disabled={selectedClaimType === 'first-party'}
            InputProps={{
              readOnly: selectedClaimType === 'first-party',
              style: { fontFamily: "var(--font-noto-sans)" }
            }}
            id="personalinfo_expirydate-input"
            data-test-id="test-personalinfo_expirydate-input"
            aria-label="Select expiry date"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PersonalInfo; 